import React from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaGlobeAmericas, FaCheckCircle, FaHandshake } from 'react-icons/fa';
import CallToAction from '../components/shared/CallToAction';

const AboutPage = () => {
  const values = [
    {
      icon: <FaUsers className="text-primary text-4xl mb-4" />,
      title: "Expertise",
      description: "Notre équipe possède une connaissance approfondie du marché canadien et des processus d'importation."
    },
    {
      icon: <FaGlobeAmericas className="text-primary text-4xl mb-4" />,
      title: "Multiculturel",
      description: "Nous comprenons les nuances culturelles et linguistiques du marché canadien bilingue."
    },
    {
      icon: <FaCheckCircle className="text-primary text-4xl mb-4" />,
      title: "Qualité",
      description: "Nous maintenons les plus hauts standards de qualité et de conformité dans tous nos services."
    },
    {
      icon: <FaHandshake className="text-primary text-4xl mb-4" />,
      title: "Partenariat",
      description: "Nous développons des relations à long terme basées sur la confiance et le succès mutuel."
    }
  ];

  const team = [
    {
      position: "Directeur Général",
      bio: "Plus de 15 ans d'expérience dans la distribution internationale et l'importation."
    },
    {
      position: "Directrice des Opérations",
      bio: "Experte en logistique et en optimisation de la chaîne d'approvisionnement."
    },
    {
      position: "Responsable Commercial",
      bio: "Spécialiste du développement commercial avec une solide connaissance du marché canadien."
    },
    {
      position: "Responsable Conformité",
      bio: "Experte en réglementation canadienne et en normes d'importation."
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">À Propos de Nous</h1>
          <p className="text-xl max-w-2xl">
            DZF Distribution a été fondée par des experts en distribution et logistique pour faciliter l'accès au marché canadien.
          </p>
        </div>
      </div>

      {/* Our Story */}
      <section className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Notre Histoire</h2>
              <p className="text-gray-600 mb-4">
                DZF Distribution est née de la vision de créer un pont entre les produits internationaux de qualité et le marché canadien en pleine croissance.
              </p>
              <p className="text-gray-600 mb-4">
                Notre fondateur a identifié un besoin essentiel: offrir aux fabricants étrangers une solution complète pour naviguer dans les complexités du marché canadien, des réglementations douanières à la distribution nationale.
              </p>
              <p className="text-gray-600">
                Aujourd'hui, nous sommes fiers d'avoir aidé des dizaines d'entreprises à établir et développer leur présence au Canada, en créant des partenariats durables qui ont mené à une croissance significative pour nos clients.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="/images/about-us.png" 
                alt="DZF Distribution Story" 
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-secondary text-white p-6 rounded-lg shadow-lg max-w-xs">
                <p className="font-bold text-lg">+250% de croissance</p>
                <p>depuis notre création en 2023</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section bg-gray-100">
        <div className="container-custom">
          <h2 className="section-title">Nos Valeurs</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-12">
            Ces principes guident chacune de nos actions et décisions pour offrir un service exceptionnel.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-lg shadow-md text-center"
              >
                <div className="flex justify-center">{value.icon}</div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section - Temporarily Hidden
      <section className="section">
        <div className="container-custom">
          <h2 className="section-title">Notre Équipe</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-12">
            Des professionnels passionnés qui mettent leur expertise à votre service.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <p className="text-secondary font-medium text-xl mb-3">{member.position}</p>
                <p className="text-gray-600">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Map/Geographical Presence */}
      <section className="section bg-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Notre Présence Géographique</h2>
              <p className="text-gray-600 mb-4">
                Stratégiquement basés à Montréal, nous couvrons efficacement les principaux marchés du Québec et de l'Ontario, avec une expansion prévue dans d'autres provinces.
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start">
                  <div className="bg-primary/10 p-2 rounded-full mr-4 mt-1">
                    <FaCheckCircle className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold">Québec</h4>
                    <p className="text-gray-600">Couverture complète de la province avec une forte présence à Montréal, Québec et Gatineau.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-primary/10 p-2 rounded-full mr-4 mt-1">
                    <FaCheckCircle className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold">Ontario</h4>
                    <p className="text-gray-600">Réseau établi à Toronto, Ottawa et dans les principales zones urbaines.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-primary/10 p-2 rounded-full mr-4 mt-1">
                    <FaCheckCircle className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold">Expansion en cours</h4>
                    <p className="text-gray-600">Développement actif vers les provinces maritimes et l'Ouest canadien.</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/dzf_geo_network.jpg" 
                alt="DZF Distribution Canada Network" 
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <CallToAction />
    </div>
  );
};

export default AboutPage; 